// import * as UserApi from '../api/user';

// import { toastr } from 'react-redux-toastr';
import { Dispatch } from 'redux';

import api from '../../api';
import { ResetDataAction, ACTIONS_RESET_DATA } from './common';
import { AuthTokenData, AuthUser, Dict } from '../../api/models';
import { RouteKeys } from '../../containers/routes/route-keys';
import { State } from '../interfaces';

const ACTION_LOGIN_REQUEST = 'AUTH/LOGIN_REQUEST';
const ACTION_LOGIN_SUCCESS = 'AUTH/LOGIN_SUCCESS';
const ACTION_LOGIN_ERROR = 'AUTH/LOGIN_ERROR';

const ACTIONS_REDIRECT_SAVE = 'AUTH/REDIRECT_SAVE';
const ACTIONS_REDIRECT_APPLY = 'AUTH/REDIRECT_APPLY';

// forgot-password
const ACTION_FORGOT_PASSWORD_REQUEST = 'AUTH/FORGOT_PASSWORD_REQUEST';
const ACTION_FORGOT_PASSWORD_SUCCESS = 'AUTH/FORGOT_PASSWORD_SUCCESS';
const ACTION_FORGOT_PASSWORD_ERROR = 'AUTH/FORGOT_PASSWORD_ERROR';

// reset-password
const ACTION_RESET_PASSWORD_REQUEST = 'AUTH/RESET_PASSWORD_REQUEST';
const ACTION_RESET_PASSWORD_SUCCESS = 'AUTH/RESET_PASSWORD_SUCCESS';
const ACTION_RESET_PASSWORD_ERROR = 'AUTH/RESET_PASSWORD_ERROR';

// logout
const ACTION_LOGOUT = 'AUTH/LOGOUT';

interface LoginRequestAction {
  type: typeof ACTION_LOGIN_REQUEST;
  data?: {
    username: string;
    password: string;
  };
}

interface LoginSuccessAction {
  type: typeof ACTION_LOGIN_SUCCESS;
  data?: AuthTokenData;
}

interface LoginErrorAction {
  type: typeof ACTION_LOGIN_ERROR;
}

// forgot-password

interface ForgotPasswordRequestAction {
  type: typeof ACTION_FORGOT_PASSWORD_REQUEST;
  data?: {
    username: string;
  };
}

interface ForgotPasswordSuccessAction {
  type: typeof ACTION_FORGOT_PASSWORD_SUCCESS;
  data?: { userId: string };
}

interface ForgotPasswordErrorAction {
  type: typeof ACTION_FORGOT_PASSWORD_ERROR;
}

// reset-password

interface ResetPasswordRequestAction {
  type: typeof ACTION_RESET_PASSWORD_REQUEST;
  data?: {
    otp: string;
    password: string;
    userId: string;
  };
}

interface ResetPasswordSuccessAction {
  type: typeof ACTION_RESET_PASSWORD_SUCCESS;
  data?: { success: boolean };
}

interface ResetPasswordErrorAction {
  type: typeof ACTION_RESET_PASSWORD_ERROR;
}

interface LogoutAction {
  type: typeof ACTION_LOGOUT;
}

interface RedirectSaveAction {
  type: typeof ACTIONS_REDIRECT_SAVE;
  data: string;
}

interface RedirectApplyAction {
  type: typeof ACTIONS_REDIRECT_APPLY;
}

type Actions =
  | LoginRequestAction
  | LoginSuccessAction
  | LoginErrorAction
  | LogoutAction
  | ResetDataAction
  | RedirectSaveAction
  | RedirectApplyAction
  | ForgotPasswordSuccessAction
  | ForgotPasswordRequestAction
  | ForgotPasswordErrorAction
  | ResetPasswordRequestAction
  | ResetPasswordSuccessAction
  | ResetPasswordErrorAction;

export interface AuthState {
  token?: string;
  username?: string;
  redirectTo: string;
  userId?: string;
  user?: AuthUser;
  apiPermissions?: Dict<boolean>;
  otp?: string;
  password?: string;
  success?: boolean;
}

const initialState: AuthState = {
  redirectTo: RouteKeys.Home,
};

// reducer
export default (
  state: AuthState = initialState,
  action: Actions
): AuthState => {
  switch (action.type) {
    // login
    case ACTION_LOGIN_REQUEST:
      return {
        ...state,
        username: action.data?.username,
      };
    case ACTION_LOGIN_SUCCESS:
      return {
        ...state,
        token: action.data?.token,
        user: action.data?.user,
        userId: action.data?.userId,
        apiPermissions: action.data?.apiPermissions,
      };
    case ACTION_LOGIN_ERROR:
      return {
        ...state,
        token: undefined,
        user: undefined,
        userId: undefined,
        apiPermissions: undefined,
      };

    // forgot-password
    case ACTION_FORGOT_PASSWORD_REQUEST:
      return {
        ...state,
        username: action.data?.username,
      };
    case ACTION_FORGOT_PASSWORD_SUCCESS:
      return {
        ...state,
        userId: action.data?.userId,
      };
    case ACTION_FORGOT_PASSWORD_ERROR:
      return {
        ...state,
        userId: undefined,
      };

    // reset-password
    case ACTION_RESET_PASSWORD_REQUEST:
      return {
        ...state,
        otp: action.data?.otp,
        password: action.data?.password,
        userId: action.data?.userId,
      };
    case ACTION_RESET_PASSWORD_SUCCESS:
      return {
        ...state,
        success: action.data?.success,
      };
    case ACTION_RESET_PASSWORD_ERROR:
      return {
        ...state,
        success: false,
      };

    // logout
    case ACTION_LOGOUT:
      api.setToken(undefined);
      return {
        ...state,
        token: undefined,
      };
    case ACTIONS_RESET_DATA:
      return {
        ...initialState,
        username: state.username,
      };

    // redirect
    case ACTIONS_REDIRECT_SAVE:
      return { ...state, redirectTo: action.data };
    case ACTIONS_REDIRECT_APPLY:
      return { ...state, redirectTo: RouteKeys.Home };

    default:
      return state;
  }
};

// action creators & async actions
export const actions = {
  login: (
    data: {
      username: string;
      password: string;
    },
    onSuccess?: () => void,
    onError?: (message?: string) => void
  ) => async (dispatch: Dispatch) => {
    dispatch({
      type: ACTION_LOGIN_REQUEST,
      data: {
        username: data.username,
      },
    });
    try {
      const response = await api.user.login({
        // username: `${data.countryCode}${data.startingCode}${data.phoneNumber}`,
        username: data.username,
        password: data.password,
      });
      dispatch({ type: ACTION_LOGIN_SUCCESS, data: response });
      api.setToken(response.token);
      if (onSuccess) onSuccess();
    } catch (error) {
      const errorMessage = 'Invalid Username or Password!';
      dispatch({
        type: ACTION_LOGIN_ERROR,
        data: { errorMessage: 'Invalid Username or Password!' },
      });
      if (onError) {
        onError(errorMessage);
      }
    }
  },

  forgotPassword: (
    data: {
      username: string;
    },
    onSuccess?: () => void,
    onError?: (message?: string) => void
  ) => async (dispatch: Dispatch) => {
    dispatch({
      type: ACTION_FORGOT_PASSWORD_REQUEST,
      data: {
        username: data.username,
      },
    });
    try {
      const response = await api.user.forgotPassword({
        username: data.username,
      });
      dispatch({ type: ACTION_FORGOT_PASSWORD_SUCCESS, data: response });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch({
        type: ACTION_FORGOT_PASSWORD_ERROR,
        data: { errorMessage: error.message },
      });
      if (onError) {
        onError(error.message);
      }
    }
  },
  resetPassword: (
    data: {
      otp: string;
      password: string;
    },
    onSuccess?: () => void,
    onError?: (message?: string) => void
  ) => async (dispatch: Dispatch, getState: () => State) => {
    dispatch({
      type: ACTION_RESET_PASSWORD_REQUEST,
      data: {
        otp: data.otp,
        password: data.password,
        userId: getState().authUser.userId,
      },
    });
    try {
      const response = await api.user.resetPassword({
        otp: data.otp,
        password: data.password,
        userId: getState().authUser.userId,
      });
      dispatch({ type: ACTION_RESET_PASSWORD_SUCCESS, data: response });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch({
        type: ACTION_RESET_PASSWORD_ERROR,
        data: { errorMessage: error.message },
      });
      if (onError) {
        onError(error.message);
      }
    }
  },

  logout: () => async (dispatch: Dispatch) => {
    dispatch({ type: ACTION_LOGOUT });
  },
  redirectSave: (to: string) => async (dispatch: Dispatch) => {
    dispatch({ type: ACTIONS_REDIRECT_SAVE, data: to });
  },

  redirectApply: () => (dispatch: Dispatch) =>
    dispatch({ type: ACTIONS_REDIRECT_APPLY }),
};
